import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { UserPlus, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const LIBELLE_MODULE = {
  vente: 'Vente',
  logistique: 'Opérations',
  marketing: 'Marketing',
  rh: 'RH'
};

const COULEUR_MODULE = {
  vente: '#3B82F6',
  logistique: '#10B981',
  marketing: '#EC4899',
  rh: '#8B5CF6'
};

const Employes = () => {
  const [employes, setEmployes] = useState([]);
  const [postes, setPostes] = useState({});
  const [chargement, setChargement] = useState(true);
  const [formOuvert, setFormOuvert] = useState(false);
  const [erreur, setErreur] = useState('');
  const [enEdition, setEnEdition] = useState(null);
  const [employeCourant, setEmployeCourant] = useState(null);
  const [accesOuvert, setAccesOuvert] = useState(false);
  const [acces, setAcces] = useState({ email: '', password: '' });
  const [editEmail, setEditEmail] = useState(false);
  const [nouvelEmail, setNouvelEmail] = useState('');
  const [nouveau, setNouveau] = useState({
    nom: '', prenom: '', telephone: '', email_perso: '',
    date_embauche: '', module: 'vente', poste: 'Commercial'
  });

  const charger = async () => {
    try {
      const [rEmp, rPostes] = await Promise.all([
        axios.get(`${API}/api/employes`),
        axios.get(`${API}/api/employes/postes`)
      ]);
      setEmployes(rEmp.data.data || []);
      setPostes(rPostes.data.data || {});
    } catch (e) {
      setErreur("Impossible de charger les employés");
    } finally {
      setChargement(false);
    }
  };

  useEffect(() => { charger(); }, []);

  const changerModule = (mod) => {
    const liste = postes[mod] || [];
    setNouveau({ ...nouveau, module: mod, poste: liste[0] || '' });
  };

  const ouvrirEdition = (emp) => {
    setErreur('');
    setEnEdition(emp.id);
    setEmployeCourant(emp);
    setNouveau({
      nom: emp.nom || '', prenom: emp.prenom || '', telephone: emp.telephone || '',
      email_perso: emp.email_perso || '', date_embauche: emp.date_embauche || '',
      module: emp.module || 'vente', poste: emp.poste || ''
    });
    setFormOuvert(true);
  };

  const fermer = () => {
    setFormOuvert(false);
    setEnEdition(null);
    setEmployeCourant(null);
    setAccesOuvert(false);
    setAcces({ email: '', password: '' });
    setEditEmail(false);
    setNouvelEmail('');
    setErreur('');
    setNouveau({ nom: '', prenom: '', telephone: '', email_perso: '', date_embauche: '', module: 'vente', poste: 'Commercial' });
  };

  const creerAcces = async () => {
    setErreur('');
    if (!acces.email.trim() || !acces.email.includes('@')) { setErreur('Email professionnel invalide'); return; }
    if (acces.password.length < 6) { setErreur('Mot de passe : 6 caractères minimum'); return; }
    try {
      await axios.post(`${API}/api/employes/${enEdition}/acces`, acces);
      setAccesOuvert(false);
      setAcces({ email: '', password: '' });
      fermer();
      charger();
    } catch (e) {
      setErreur(e.response?.data?.detail || "Erreur lors de la création de l'accès");
    }
  };

  const modifierEmailPro = async () => {
    setErreur('');
    if (!nouvelEmail.trim() || !nouvelEmail.includes('@')) { setErreur('Email invalide'); return; }
    try {
      await axios.put(`${API}/api/employes/${enEdition}/email-pro`, { email: nouvelEmail });
      setEditEmail(false);
      fermer();
      charger();
    } catch (e) {
      setErreur(e.response?.data?.detail || "Erreur lors de la modification");
    }
  };

  const enregistrer = async () => {
    setErreur('');
    if (!nouveau.prenom.trim()) { setErreur('Le prénom est obligatoire'); return; }
    if (!nouveau.nom.trim()) { setErreur('Le nom est obligatoire'); return; }
    if (!nouveau.telephone.trim()) { setErreur('Le téléphone est obligatoire'); return; }
    if (!nouveau.module) { setErreur('Le module est obligatoire'); return; }
    if (!nouveau.poste) { setErreur('Le poste est obligatoire'); return; }
    try {
      if (enEdition) {
        await axios.put(`${API}/api/employes/${enEdition}`, nouveau);
      } else {
        await axios.post(`${API}/api/employes`, nouveau);
      }
      fermer();
      charger();
    } catch (e) {
      setErreur(e.response?.data?.detail || "Erreur lors de l'enregistrement");
    }
  };

  const champ = { width: '100%', padding: '10px', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '14px', marginBottom: '12px' };

  return (
    <div style={{ padding: '8px' }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
        <button onClick={() => setFormOuvert(true)}
          style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#8B5CF6', color: '#fff', border: 'none', borderRadius: '8px', padding: '10px 16px', fontSize: '14px', cursor: 'pointer' }}>
          <UserPlus size={16} /> Nouvel employé
        </button>
      </div>

      {erreur && !formOuvert && (
        <div style={{ background: '#FEE2E2', color: '#991B1B', padding: '12px', borderRadius: '8px', marginBottom: '16px' }}>{erreur}</div>
      )}

      <div style={{ background: '#fff', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
        {chargement ? (
          <div style={{ padding: '32px', textAlign: 'center', color: '#6B7280' }}>Chargement…</div>
        ) : employes.length === 0 ? (
          <div style={{ padding: '32px', textAlign: 'center', color: '#6B7280' }}>Aucun employé enregistré</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#F9FAFB', textAlign: 'left' }}>
                <th style={{ padding: '12px 16px', fontSize: '12px', color: '#6B7280' }}>NOM</th>
                <th style={{ padding: '12px 16px', fontSize: '12px', color: '#6B7280' }}>MODULE</th>
                <th style={{ padding: '12px 16px', fontSize: '12px', color: '#6B7280' }}>POSTE</th>
                <th style={{ padding: '12px 16px', fontSize: '12px', color: '#6B7280' }}>TÉLÉPHONE</th>
                <th style={{ padding: '12px 16px', fontSize: '12px', color: '#6B7280' }}>ACCÈS CRM</th>
              </tr>
            </thead>
            <tbody>
              {employes.map((e) => (
                <tr key={e.id} onClick={() => ouvrirEdition(e)} style={{ borderTop: '1px solid #F3F4F6', cursor: 'pointer' }}>
                  <td style={{ padding: '14px 16px', fontWeight: 500 }}>{e.prenom} {e.nom}</td>
                  <td style={{ padding: '14px 16px' }}>
                    <span style={{ background: (COULEUR_MODULE[e.module] || '#6B7280') + '22', color: COULEUR_MODULE[e.module] || '#6B7280', padding: '4px 10px', borderRadius: '999px', fontSize: '12px' }}>
                      {LIBELLE_MODULE[e.module] || e.module}
                    </span>
                  </td>
                  <td style={{ padding: '14px 16px', color: '#374151' }}>{e.poste}</td>
                  <td style={{ padding: '14px 16px', color: '#6B7280' }}>{e.telephone || '—'}</td>
                  <td style={{ padding: '14px 16px', color: e.user_id ? '#10B981' : '#9CA3AF', fontSize: '13px' }}>
                    {e.user_id ? 'Oui' : 'Non'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {formOuvert && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}>
          <div style={{ background: '#fff', borderRadius: '12px', padding: '24px', width: '440px', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, fontSize: '18px' }}>{enEdition ? 'Modifier l\u2019employé' : 'Nouvel employé'}</h3>
              <X size={20} style={{ cursor: 'pointer', color: '#6B7280' }} onClick={fermer} />
            </div>

            {erreur && <div style={{ background: '#FEE2E2', color: '#991B1B', padding: '10px', borderRadius: '8px', marginBottom: '12px', fontSize: '13px' }}>{erreur}</div>}

            <input style={champ} placeholder="Prénom *" value={nouveau.prenom} onChange={(ev) => setNouveau({ ...nouveau, prenom: ev.target.value })} />
            <input style={champ} placeholder="Nom *" value={nouveau.nom} onChange={(ev) => setNouveau({ ...nouveau, nom: ev.target.value })} />
            <input style={champ} placeholder="Téléphone *" value={nouveau.telephone} onChange={(ev) => setNouveau({ ...nouveau, telephone: ev.target.value })} />
            <input style={champ} placeholder="Email personnel / privé (facultatif)" value={nouveau.email_perso} onChange={(ev) => setNouveau({ ...nouveau, email_perso: ev.target.value })} />

            <label style={{ fontSize: '13px', color: '#6B7280' }}>Date d'embauche (facultatif)</label>
            <input style={champ} type="date" value={nouveau.date_embauche} onChange={(ev) => setNouveau({ ...nouveau, date_embauche: ev.target.value })} />

            <label style={{ fontSize: '13px', color: '#6B7280' }}>Module *</label>
            <select style={champ} value={nouveau.module} onChange={(ev) => changerModule(ev.target.value)}>
              {Object.keys(postes).map((m) => <option key={m} value={m}>{LIBELLE_MODULE[m] || m}</option>)}
            </select>

            <label style={{ fontSize: '13px', color: '#6B7280' }}>Poste *</label>
            <select style={champ} value={nouveau.poste} onChange={(ev) => setNouveau({ ...nouveau, poste: ev.target.value })}>
              {(postes[nouveau.module] || []).map((p) => <option key={p} value={p}>{p}</option>)}
            </select>

            {enEdition && !employeCourant?.user_id && !accesOuvert && (
              <button onClick={() => { setAccesOuvert(true); setErreur(''); }}
                style={{ width: '100%', padding: '10px', border: '1px solid #8B5CF6', background: '#F5F3FF', color: '#6D28D9', borderRadius: '8px', cursor: 'pointer', marginBottom: '12px' }}>
                Créer un accès CRM
              </button>
            )}

            {enEdition && employeCourant?.user_id && !editEmail && (
              <div style={{ padding: '10px 12px', background: '#ECFDF5', borderRadius: '8px', marginBottom: '12px' }}>
                <div style={{ fontSize: '12px', color: '#047857', marginBottom: '2px' }}>Email professionnel (connexion)</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px' }}>
                  <span style={{ color: '#065F46', fontSize: '14px' }}>{employeCourant?.email_connexion || '—'}</span>
                  <span onClick={() => { setNouvelEmail(employeCourant?.email_connexion || ''); setEditEmail(true); setErreur(''); }}
                    style={{ color: '#6D28D9', fontSize: '13px', cursor: 'pointer', textDecoration: 'underline', whiteSpace: 'nowrap' }}>
                    Modifier
                  </span>
                </div>
              </div>
            )}

            {editEmail && (
              <div style={{ padding: '14px', background: '#F9FAFB', borderRadius: '8px', marginBottom: '12px' }}>
                <div style={{ fontSize: '13px', color: '#374151', marginBottom: '4px', fontWeight: 500 }}>Modifier l'email professionnel</div>
                <div style={{ fontSize: '12px', color: '#92400E', background: '#FEF3C7', padding: '8px', borderRadius: '6px', marginBottom: '10px' }}>
                  C'est son identifiant de connexion : pensez à le prévenir.
                </div>
                <input style={champ} value={nouvelEmail} onChange={(ev) => setNouvelEmail(ev.target.value)} />
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button onClick={() => setEditEmail(false)} style={{ flex: 1, padding: '9px', border: '1px solid #D1D5DB', background: '#fff', borderRadius: '8px', cursor: 'pointer' }}>Annuler</button>
                  <button onClick={modifierEmailPro} style={{ flex: 1, padding: '9px', border: 'none', background: '#8B5CF6', color: '#fff', borderRadius: '8px', cursor: 'pointer' }}>Valider</button>
                </div>
              </div>
            )}

            {accesOuvert && (
              <div style={{ padding: '14px', background: '#F9FAFB', borderRadius: '8px', marginBottom: '12px' }}>
                <div style={{ fontSize: '13px', color: '#374151', marginBottom: '10px', fontWeight: 500 }}>Nouvel accès CRM</div>
                <input style={champ} placeholder="Email professionnel *" value={acces.email} onChange={(ev) => setAcces({ ...acces, email: ev.target.value })} />
                <input style={champ} type="text" placeholder="Mot de passe (6 car. min) *" value={acces.password} onChange={(ev) => setAcces({ ...acces, password: ev.target.value })} />
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button onClick={() => setAccesOuvert(false)} style={{ flex: 1, padding: '9px', border: '1px solid #D1D5DB', background: '#fff', borderRadius: '8px', cursor: 'pointer' }}>Annuler</button>
                  <button onClick={creerAcces} style={{ flex: 1, padding: '9px', border: 'none', background: '#8B5CF6', color: '#fff', borderRadius: '8px', cursor: 'pointer' }}>Créer l'accès</button>
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
              <button onClick={fermer} style={{ flex: 1, padding: '10px', border: '1px solid #D1D5DB', background: '#fff', borderRadius: '8px', cursor: 'pointer' }}>Annuler</button>
              <button onClick={enregistrer} style={{ flex: 1, padding: '10px', border: 'none', background: '#8B5CF6', color: '#fff', borderRadius: '8px', cursor: 'pointer' }}>Enregistrer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Employes;
